import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  createOrUpdateAttendeeProfile,
  createOrUpdateEmergencyContact,
  createOrUpdateChurchInfo,
  createOrUpdateFaithInfo,
  getAttendeeProfile,
  markProfileAsCompleted,
} from '../lib/api/profiles';
import ChurchNameInput from '../components/ChurchNameInput';
import { captureError } from '../lib/capture-error';
import { resolveProfilePhone } from '../lib/event-application';
import './ProfileCompletionPage.css';

const ProfileCompletionPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('return') || '/dashboard';
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);

  // Step 1: Personal Information
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('United States');
  const [homePhone, setHomePhone] = useState('');
  const [cellPhone, setCellPhone] = useState('');
  const [workPhone, setWorkPhone] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState<'Male' | 'Female' | ''>('');
  const [tshirtSize, setTshirtSize] = useState('');

  // Step 2: Emergency Contact
  const [emergFirstName, setEmergFirstName] = useState('');
  const [emergLastName, setEmergLastName] = useState('');
  const [emergAddress1, setEmergAddress1] = useState('');
  const [emergAddress2, setEmergAddress2] = useState('');
  const [emergCity, setEmergCity] = useState('');
  const [emergState, setEmergState] = useState('');
  const [emergZip, setEmergZip] = useState('');
  const [emergCountry, setEmergCountry] = useState('United States');
  const [emergPhone, setEmergPhone] = useState('');

  // Step 3: Church Information
  const [churchName, setChurchName] = useState('');
  const [pastorFirstName, setPastorFirstName] = useState('');
  const [pastorLastName, setPastorLastName] = useState('');
  const [churchAddress1, setChurchAddress1] = useState('');
  const [churchAddress2, setChurchAddress2] = useState('');
  const [churchCity, setChurchCity] = useState('');
  const [churchState, setChurchState] = useState('');
  const [churchZip, setChurchZip] = useState('');
  const [churchCountry, setChurchCountry] = useState('United States');
  const [churchPhone, setChurchPhone] = useState('');

  // Step 4: Faith Information
  const [isBornAgain, setIsBornAgain] = useState<boolean | null>(null);
  const [believerLength, setBelieverLength] = useState('');
  const [testimony, setTestimony] = useState('');
  const [evangelismExperience, setEvangelismExperience] = useState('');
  const [missionTripExperience, setMissionTripExperience] = useState('');
  const [hasBeenOnMissionTrip, setHasBeenOnMissionTrip] = useState<boolean | null>(null);
  const [currentlyPracticingEvangelism, setCurrentlyPracticingEvangelism] = useState<boolean | null>(null);

  useEffect(() => {
    const loadExistingProfile = async () => {
      if (!user) {
        setLoadingProfile(false);
        return;
      }

      try {
        const profile = await getAttendeeProfile(user.id);
        if (!profile) {
          setLoadingProfile(false);
          return;
        }

        setProfileId(profile.id);
        setFirstName(profile.first_name || '');
        setLastName(profile.last_name || '');
        setAddressLine1(profile.address_line1 || '');
        setAddressLine2(profile.address_line2 || '');
        setCity(profile.city || '');
        setState(profile.state || '');
        setZip(profile.zip || '');
        setCountry(profile.country || 'United States');
        setHomePhone(profile.home_phone || '');
        setCellPhone(resolveProfilePhone(profile) || profile.cell_phone || '');
        setWorkPhone(profile.work_phone || '');
        setEmail(profile.email || user.email || '');
        if (profile.date_of_birth) {
          const dob = new Date(profile.date_of_birth);
          if (!Number.isNaN(dob.getTime())) {
            setDateOfBirth(dob.toISOString().slice(0, 10));
          }
        }
        setGender(profile.gender || '');
        setTshirtSize(profile.tshirt_size || '');

        if (profile.emergency_contact) {
          setEmergFirstName(profile.emergency_contact.first_name || '');
          setEmergLastName(profile.emergency_contact.last_name || '');
          setEmergAddress1(profile.emergency_contact.address_line1 || '');
          setEmergAddress2(profile.emergency_contact.address_line2 || '');
          setEmergCity(profile.emergency_contact.city || '');
          setEmergState(profile.emergency_contact.state || '');
          setEmergZip(profile.emergency_contact.zip || '');
          setEmergCountry(profile.emergency_contact.country || 'United States');
          setEmergPhone(profile.emergency_contact.phone_number || '');
        }

        if (profile.church_info) {
          setChurchName(profile.church_info.church_name || '');
          setPastorFirstName(profile.church_info.pastor_first_name || '');
          setPastorLastName(profile.church_info.pastor_last_name || '');
          setChurchAddress1(profile.church_info.address_line1 || '');
          setChurchAddress2(profile.church_info.address_line2 || '');
          setChurchCity(profile.church_info.city || '');
          setChurchState(profile.church_info.state || '');
          setChurchZip(profile.church_info.zip || '');
          setChurchCountry(profile.church_info.country || 'United States');
          setChurchPhone(profile.church_info.phone_number || '');
        }

        if (profile.faith_info) {
          setIsBornAgain(profile.faith_info.is_born_again_believer);
          setBelieverLength(profile.faith_info.believer_length || '');
          setTestimony(profile.faith_info.personal_testimony || '');
          setEvangelismExperience(profile.faith_info.evangelism_experience || '');
          setMissionTripExperience(profile.faith_info.mission_trip_experience || '');
          setHasBeenOnMissionTrip(profile.faith_info.has_been_on_mission_trip);
          setCurrentlyPracticingEvangelism(profile.faith_info.currently_practicing_evangelism);
        }
      } catch (err) {
        captureError(err, 'Failed to load profile for completion');
      } finally {
        setLoadingProfile(false);
      }
    };

    loadExistingProfile();
  }, [user]);

  async function handleStep1Submit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;

    setSaving(true);
    setError(null);

    try {
      const profile = await createOrUpdateAttendeeProfile(user.id, {
        first_name: firstName,
        last_name: lastName,
        address_line1: addressLine1 || null,
        address_line2: addressLine2 || null,
        city: city || null,
        state: state || null,
        zip: zip || null,
        country: country || null,
        home_phone: homePhone || null,
        cell_phone: cellPhone,
        work_phone: workPhone || null,
        email,
        date_of_birth: dateOfBirth,
        gender: gender || null,
        tshirt_size: tshirtSize || null,
      });

      setProfileId(profile.id);
      setStep(2);
    } catch (err) {
      setError('Failed to save personal information');
      captureError(err);
    } finally {
      setSaving(false);
    }
  }

  async function handleStep2Submit(e: React.FormEvent) {
    e.preventDefault();
    if (!profileId) return;

    setSaving(true);
    setError(null);

    try {
      await createOrUpdateEmergencyContact(profileId, {
        first_name: emergFirstName,
        last_name: emergLastName,
        address_line1: emergAddress1 || null,
        address_line2: emergAddress2 || null,
        city: emergCity || null,
        state: emergState || null,
        zip: emergZip || null,
        country: emergCountry || null,
        phone_number: emergPhone,
      });

      setStep(3);
    } catch (err) {
      setError('Failed to save emergency contact');
      captureError(err);
    } finally {
      setSaving(false);
    }
  }

  async function handleStep3Submit(e: React.FormEvent) {
    e.preventDefault();
    if (!profileId) return;

    setSaving(true);
    setError(null);

    try {
      await createOrUpdateChurchInfo(profileId, {
        church_name: churchName || null,
        pastor_first_name: pastorFirstName,
        pastor_last_name: pastorLastName,
        address_line1: churchAddress1 || null,
        address_line2: churchAddress2 || null,
        city: churchCity || null,
        state: churchState || null,
        zip: churchZip || null,
        country: churchCountry || null,
        phone_number: churchPhone,
      });

      setStep(4);
    } catch (err) {
      setError('Failed to save church information');
      captureError(err);
    } finally {
      setSaving(false);
    }
  }

  async function handleStep4Submit(e: React.FormEvent) {
    e.preventDefault();
    if (!profileId || isBornAgain === null) return;

    setSaving(true);
    setError(null);

    try {
      await createOrUpdateFaithInfo(profileId, {
        is_born_again_believer: isBornAgain,
        believer_length: believerLength || null,
        personal_testimony: testimony || null,
        evangelism_experience: evangelismExperience || null,
        mission_trip_experience: missionTripExperience || null,
        has_been_on_mission_trip: hasBeenOnMissionTrip,
        currently_practicing_evangelism: currentlyPracticingEvangelism,
      });

      await markProfileAsCompleted(profileId);
      navigate(returnUrl.startsWith('/') ? returnUrl : '/dashboard');
    } catch (err) {
      setError('Failed to save faith information');
      captureError(err);
    } finally {
      setSaving(false);
    }
  }

  const totalSteps = 4;

  if (loadingProfile) {
    return (
      <div className="container my-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <h1 className="text-center mb-4">Complete Your Profile</h1>

          {/* Progress Bar */}
          <div className="progress mb-4" style={{ height: '8px' }}>
            <div
              className="progress-bar"
              role="progressbar"
              style={{ width: `${(step / totalSteps) * 100}%` }}
              aria-valuenow={step}
              aria-valuemin={1}
              aria-valuemax={totalSteps}
            ></div>
          </div>

          <div className="text-center mb-4">
            <span className="badge bg-primary">Step {step} of {totalSteps}</span>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Step 1: Personal Information */}
          {step === 1 && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Personal Information</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleStep1Submit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">First Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Last Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Address Line 1</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addressLine1}
                        onChange={(e) => setAddressLine1(e.target.value)}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Address Line 2</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addressLine2}
                        onChange={(e) => setAddressLine2(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className="form-control"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">State/Province</label>
                      <input
                        type="text"
                        className="form-control"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">ZIP/Postal Code</label>
                      <input
                        type="text"
                        className="form-control"
                        value={zip}
                        onChange={(e) => setZip(e.target.value)}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        className="form-control"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Home Phone</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={homePhone}
                        onChange={(e) => setHomePhone(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Cell Phone *</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={cellPhone}
                        onChange={(e) => setCellPhone(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Work Phone</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={workPhone}
                        onChange={(e) => setWorkPhone(e.target.value)}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        className="form-control"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Date of Birth *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={dateOfBirth}
                        onChange={(e) => setDateOfBirth(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Gender *</label>
                      <select
                        className="form-select"
                        value={gender}
                        onChange={(e) => setGender(e.target.value as 'Male' | 'Female')}
                        required
                      >
                        <option value="">Select...</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">T-Shirt Size</label>
                      <select
                        className="form-select"
                        value={tshirtSize}
                        onChange={(e) => setTshirtSize(e.target.value)}
                      >
                        <option value="">Select...</option>
                        <option value="Small">Small</option>
                        <option value="Medium">Medium</option>
                        <option value="Large">Large</option>
                        <option value="XL">XL</option>
                        <option value="XXL">XXL</option>
                        <option value="XXXL">XXXL</option>
                      </select>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end mt-4">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {!saving && <i className="fas fa-arrow-right me-2" aria-hidden></i>}
                      {saving ? 'Saving...' : 'Next'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Step 2: Emergency Contact */}
          {step === 2 && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Emergency Contact</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleStep2Submit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">First Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={emergFirstName}
                        onChange={(e) => setEmergFirstName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Last Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={emergLastName}
                        onChange={(e) => setEmergLastName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Phone Number *</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={emergPhone}
                        onChange={(e) => setEmergPhone(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Address Line 1</label>
                      <input
                        type="text"
                        className="form-control"
                        value={emergAddress1}
                        onChange={(e) => setEmergAddress1(e.target.value)}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Address Line 2</label>
                      <input
                        type="text"
                        className="form-control"
                        value={emergAddress2}
                        onChange={(e) => setEmergAddress2(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className="form-control"
                        value={emergCity}
                        onChange={(e) => setEmergCity(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">State/Province</label>
                      <input
                        type="text"
                        className="form-control"
                        value={emergState}
                        onChange={(e) => setEmergState(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">ZIP/Postal Code</label>
                      <input
                        type="text"
                        className="form-control"
                        value={emergZip}
                        onChange={(e) => setEmergZip(e.target.value)}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        className="form-control"
                        value={emergCountry}
                        onChange={(e) => setEmergCountry(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-between mt-4">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="btn btn-outline-secondary"
                    >
                      <i className="fas fa-arrow-left me-2" aria-hidden></i>
                      Back
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {!saving && <i className="fas fa-arrow-right me-2" aria-hidden></i>}
                      {saving ? 'Saving...' : 'Next'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Step 3: Church Information */}
          {step === 3 && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Church Information</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleStep3Submit}>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Church Name</label>
                      <ChurchNameInput
                        value={churchName}
                        onChange={setChurchName}
                        className="form-control"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Pastor's First Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={pastorFirstName}
                        onChange={(e) => setPastorFirstName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Pastor's Last Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={pastorLastName}
                        onChange={(e) => setPastorLastName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Church Phone *</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={churchPhone}
                        onChange={(e) => setChurchPhone(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Address Line 1</label>
                      <input
                        type="text"
                        className="form-control"
                        value={churchAddress1}
                        onChange={(e) => setChurchAddress1(e.target.value)}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Address Line 2</label>
                      <input
                        type="text"
                        className="form-control"
                        value={churchAddress2}
                        onChange={(e) => setChurchAddress2(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className="form-control"
                        value={churchCity}
                        onChange={(e) => setChurchCity(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">State/Province</label>
                      <input
                        type="text"
                        className="form-control"
                        value={churchState}
                        onChange={(e) => setChurchState(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">ZIP/Postal Code</label>
                      <input
                        type="text"
                        className="form-control"
                        value={churchZip}
                        onChange={(e) => setChurchZip(e.target.value)}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        className="form-control"
                        value={churchCountry}
                        onChange={(e) => setChurchCountry(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-between mt-4">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="btn btn-outline-secondary"
                    >
                      <i className="fas fa-arrow-left me-2" aria-hidden></i>
                      Back
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {!saving && <i className="fas fa-arrow-right me-2" aria-hidden></i>}
                      {saving ? 'Saving...' : 'Next'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Step 4: Faith Information */}
          {step === 4 && (
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Faith Information</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleStep4Submit}>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Are You a Born Again Believer? *</label>
                      <select
                        className="form-select"
                        value={isBornAgain === null ? '' : isBornAgain ? 'yes' : 'no'}
                        onChange={(e) => setIsBornAgain(e.target.value === 'yes')}
                        required
                      >
                        <option value="">Select...</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">How Long Have You Been a Believer?</label>
                      <input
                        type="text"
                        className="form-control"
                        value={believerLength}
                        onChange={(e) => setBelieverLength(e.target.value)}
                        placeholder="e.g., 10 years, Since 2015"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Personal Testimony</label>
                      <textarea
                        className="form-control"
                        rows={4}
                        value={testimony}
                        onChange={(e) => setTestimony(e.target.value)}
                        placeholder="Briefly describe your testimony"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Evangelism Experience</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={evangelismExperience}
                        onChange={(e) => setEvangelismExperience(e.target.value)}
                        placeholder="Describe your experience with personal evangelism"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Have You Been on a Mission Trip Before?</label>
                      <select
                        className="form-select"
                        value={hasBeenOnMissionTrip === null ? '' : hasBeenOnMissionTrip ? 'yes' : 'no'}
                        onChange={(e) => setHasBeenOnMissionTrip(e.target.value === 'yes')}
                      >
                        <option value="">Select...</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                    {hasBeenOnMissionTrip && (
                      <div className="col-12">
                        <label className="form-label">Mission Trip Experience</label>
                        <textarea
                          className="form-control"
                          rows={3}
                          value={missionTripExperience}
                          onChange={(e) => setMissionTripExperience(e.target.value)}
                          placeholder="Describe your mission trip experiences"
                        />
                      </div>
                    )}
                    <div className="col-12">
                      <label className="form-label">Are You Currently Practicing Evangelism?</label>
                      <select
                        className="form-select"
                        value={currentlyPracticingEvangelism === null ? '' : currentlyPracticingEvangelism ? 'yes' : 'no'}
                        onChange={(e) => setCurrentlyPracticingEvangelism(e.target.value === 'yes')}
                      >
                        <option value="">Select...</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between mt-4">
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      className="btn btn-outline-secondary"
                    >
                      <i className="fas fa-arrow-left me-2" aria-hidden></i>
                      Back
                    </button>
                    <button type="submit" className="btn btn-success" disabled={saving}>
                      {!saving && <i className="fas fa-check me-2" aria-hidden></i>}
                      {saving ? 'Saving...' : 'Complete Profile'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionPage;
